import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Select,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowClockwise, ArrowLeft, Bank, CalendarBlank, WarningCircle } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import {
  useContaCilDashboard,
  useContaCilOperacional,
} from '../../../../hooks/useContaCilDashboard';
import { useContaCilImports } from '../../../../hooks/useContaCilImportar';

import { AtalhosNavegacionais } from './components/AtalhosNavegacionais';
import { EmptyState } from './components/EmptyState';
import { ImportarExtratoModal } from './components/ImportarExtratoModal';
import { OperacionalKpis } from './components/OperacionalKpis';
import { MesDetailPanel } from './components/MesDetailPanel';
import { MesResumoStrip } from './components/MesResumoStrip';
import { MonthVolumeGrid } from './components/MonthVolumeGrid';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [
  CURRENT_YEAR + 1,
  CURRENT_YEAR,
  CURRENT_YEAR - 1,
  CURRENT_YEAR - 2,
  CURRENT_YEAR - 3,
];

export function ConciliacaoContabilPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const importarModal = useDisclosure();
  const [modalFiltro, setModalFiltro] = useState<'recentes' | 'pendentes'>('recentes');

  const [ano, setAno] = useState<number>(CURRENT_YEAR);
  const [mesSelecionado, setMesSelecionado] = useState<number | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useContaCilDashboard({ ano });
  const { data: operacional, isLoading: operLoading } = useContaCilOperacional(ano);
  const { data: pendentes = [] } = useContaCilImports('a_identificar');

  const mesDetail = useMemo(() => {
    if (!data?.meses_status || !mesSelecionado) return null;
    return data.meses_status.find(m => m.mes === mesSelecionado) ?? null;
  }, [data, mesSelecionado]);

  const isEmpty = !isLoading && !!data?.kpis && data.kpis.lancamentos_total === 0;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['contacil', 'dashboard'] });
    refetch();
  };

  // "Importar": abre no filtro recentes. "Revisar": abre no filtro pendentes
  // (pra atribuir os 'a identificar' direto).
  const abrirImportar = () => {
    setModalFiltro('recentes');
    importarModal.onOpen();
  };
  const abrirRevisar = () => {
    setModalFiltro('pendentes');
    importarModal.onOpen();
  };

  return (
    <GlassPageBg>
      <Box maxW="1440px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={3}>
            <IconButton
              aria-label="Voltar para Contábil"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color="#64748B"
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: '#0F172A' }}
              onClick={() => navigate('/modulesHome/accountingConciliation')}
            />
            <Text
              fontSize="11px"
              color="#94A3B8"
              textTransform="uppercase"
              letterSpacing="0.1em"
              fontWeight="semibold"
            >
              Contábil / Conciliação Contábil
            </Text>
          </Flex>

          <Flex
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            wrap="wrap"
            gap={4}
            mb={3}
          >
            <Flex align="center" gap={5}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="64px"
                h="64px"
                borderRadius="2xl"
                bg="linear-gradient(135deg, #E0E7FF 0%, #DDD6FE 100%)"
                boxShadow="0 8px 24px -4px rgba(99, 102, 241, 0.32)"
                color="#6366F1"
              >
                <Bank size={34} weight="duotone" />
              </Box>
              <Box>
                <Heading
                  fontSize={{ base: '24px', md: '32px' }}
                  fontWeight="700"
                  color="#0F172A"
                  lineHeight="1.1"
                  letterSpacing="-0.02em"
                >
                  Conciliação Contábil
                </Heading>
                <Text fontSize="sm" color="#64748B" mt={1} maxW="640px" lineHeight="1.5">
                  Importe extratos bancários, categorize por regras inteligentes, vincule ao Plano
                  de Contas e exporte direto para o Domínio.
                </Text>
              </Box>
            </Flex>

            {/* Toolbar direita: ano + refresh */}
            <Flex align="center" gap={2} flexShrink={0}>
              <Flex
                align="center"
                gap={2}
                bg="rgba(255, 255, 255, 0.72)"
                backdropFilter="blur(20px) saturate(180%)"
                sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
                borderWidth="1px"
                borderColor="rgba(255, 255, 255, 0.6)"
                borderRadius="full"
                px={3}
                py={1.5}
                boxShadow="0 4px 12px -4px rgba(15, 23, 42, 0.08)"
              >
                <CalendarBlank size={16} color="#64748B" weight="duotone" />
                <Select
                  value={ano}
                  onChange={e => {
                    setAno(Number(e.target.value));
                    setMesSelecionado(null);
                  }}
                  variant="unstyled"
                  size="sm"
                  fontSize="sm"
                  fontWeight="600"
                  color="#0F172A"
                  w="auto"
                  cursor="pointer"
                >
                  {YEAR_OPTIONS.map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Select>
              </Flex>
              <Tooltip label="Atualizar" placement="top" hasArrow>
                <IconButton
                  aria-label="Atualizar dashboard"
                  icon={
                    isFetching ? (
                      <Spinner size="sm" color="#1B88F5" thickness="2px" />
                    ) : (
                      <ArrowClockwise size={18} weight="bold" />
                    )
                  }
                  size="md"
                  variant="ghost"
                  borderRadius="full"
                  bg="rgba(255, 255, 255, 0.72)"
                  backdropFilter="blur(20px) saturate(180%)"
                  sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
                  borderWidth="1px"
                  borderColor="rgba(255, 255, 255, 0.6)"
                  color="#0F172A"
                  _hover={{ bg: 'rgba(255, 255, 255, 0.92)', color: '#1B88F5' }}
                  onClick={handleRefresh}
                  isDisabled={isFetching}
                />
              </Tooltip>
            </Flex>
          </Flex>
        </motion.div>

        {/* Estado erro */}
        {isError && (
          <GlassCard intensity="medium" p={5} mt={6} mb={6}>
            <Text fontSize="sm" color="#EF4444">
              Não foi possível carregar o resumo agora. Tente novamente em instantes.
            </Text>
          </GlassCard>
        )}

        {/* Aviso: imports aguardando identificacao de empresa */}
        {pendentes.length > 0 && (
          <GlassCard intensity="medium" p={4} mt={6}>
            <Flex align="center" justify="space-between" gap={3} wrap="wrap">
              <Flex align="center" gap={3} minW={0}>
                <Box color="#F59E0B" flexShrink={0}>
                  <WarningCircle size={22} weight="duotone" />
                </Box>
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="600" color="#0F172A">
                    {pendentes.length} importação{pendentes.length > 1 ? 'ões' : ''} aguardando
                    identificação de empresa
                  </Text>
                  <Text fontSize="xs" color="#64748B">
                    O extrato foi lido, mas a empresa não foi detectada. Atribua manualmente para
                    concluir.
                  </Text>
                </Box>
              </Flex>
              <Button
                size="sm"
                colorScheme="orange"
                borderRadius="full"
                flexShrink={0}
                onClick={abrirRevisar}
              >
                Revisar
              </Button>
            </Flex>
          </GlassCard>
        )}

        {/* KPIs */}
        <Box mt={8}>
          <OperacionalKpis data={operacional} isLoading={operLoading} />
        </Box>

        {/* Calendario + Detalhe ou EmptyState */}
        {isEmpty ? (
          <Box my={8}>
            <EmptyState onImportar={abrirImportar} />
          </Box>
        ) : (
          <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6} mb={8} alignItems="start">
            <GridItem>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
              >
                <GlassCard intensity="medium" p={{ base: 5, md: 6 }}>
                  <Flex justify="space-between" align="baseline" mb={5}>
                    <Box>
                      <Text
                        fontSize="10px"
                        fontWeight="600"
                        color="#94A3B8"
                        textTransform="uppercase"
                        letterSpacing="0.08em"
                      >
                        Calendário
                      </Text>
                      <Heading
                        as="h2"
                        fontSize="md"
                        fontWeight="600"
                        color="#0F172A"
                        letterSpacing="-0.01em"
                      >
                        {ano} · Volume mensal
                      </Heading>
                    </Box>
                    {mesSelecionado && (
                      <Button
                        size="xs"
                        variant="ghost"
                        color="#64748B"
                        fontSize="11px"
                        fontWeight="600"
                        onClick={() => setMesSelecionado(null)}
                        _hover={{ color: '#1B88F5', bg: 'rgba(27, 136, 245, 0.06)' }}
                        borderRadius="full"
                      >
                        Limpar seleção
                      </Button>
                    )}
                  </Flex>
                  <MonthVolumeGrid
                    meses={data?.meses_status ?? []}
                    selecionado={mesSelecionado}
                    onSelect={setMesSelecionado}
                  />
                  <MesResumoStrip mes={mesDetail} meses={data?.meses_status ?? []} ano={ano} />
                </GlassCard>
              </motion.div>
            </GridItem>
            <GridItem>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.18, ease: [0.4, 0, 0.2, 1] }}
              >
                <MesDetailPanel mes={mesDetail} ano={ano} meses={data?.meses_status} />
              </motion.div>
            </GridItem>
          </Grid>
        )}

        {/* Atalhos pras telas futuras */}
        <AtalhosNavegacionais onImportarClick={abrirImportar} />

        {/* Footer aviso fase 1 */}
        <Box
          mt={10}
          pt={6}
          borderTopWidth="1px"
          borderColor="rgba(15, 23, 42, 0.06)"
          textAlign="center"
        >
          <Text fontSize="11px" color="#94A3B8">
            Submódulo em construção · Fase 1 · Telas operacionais chegam nas próximas iterações
          </Text>
        </Box>
      </Box>

      <ImportarExtratoModal
        isOpen={importarModal.isOpen}
        onClose={importarModal.onClose}
        initialFiltro={modalFiltro}
      />
    </GlassPageBg>
  );
}
